<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'MaDataTable',
})
</script>
<template>
	<div class="ma-data-table">
		<el-dialog :model-value="visible" :title :close-on-click-modal="false" :width="800" draggable @close="handleClose">
			<div class="table-content">
				<el-form v-if="queryParams.length > 0" ref="queryRef" :inline="true" :model="state.queryForm" style="margin-bottom: 15px">
					<el-form-item v-for="(query, index) in queryParams" :key="index">
						<ma-org-select v-if="query.type == 'org'" v-model="state.queryForm[query.field]" :placeholder="query.label" />
						<ma-org-inspect-select v-if="query.type == 'orgBiz'" v-model="state.queryForm[query.field]" :placeholder="query.label" />

						<ma-dict-select
							v-if="query.type == 'dict'"
							v-model="state.queryForm[query.field]"
							:dict-type="query.dict"
							:clearable="true"
							:placeholder="query.label"
						/>
						<el-input
							v-if="query.type == 'input'"
							v-model="state.queryForm[query.field]"
							:placeholder="query.label"
							@keyup.enter="getDataList"
						></el-input>
					</el-form-item>
					<el-form-item>
						<el-button icon="Search" type="primary" size="small" @click="getDataList()">查询</el-button>
						<el-button icon="RefreshRight" size="small" @click="handleReset">重置</el-button>
					</el-form-item>
				</el-form>
				<el-table
					ref="tableRef"
					v-loading="state.dataListLoading"
					:data="state.dataList"
					border
					style="width: 100%"
					height="100%"
					highlight-current-row
					show-overflow-tooltip
					:row-key="rowKey"
					@selection-change="selectionChangeHandle"
					@current-change="currentChange"
				>
					<el-table-column
						v-if="multiple"
						type="selection"
						:reserve-selection="rowKey === '' ? false : true"
						header-align="center"
						align="center"
						width="50"
					></el-table-column>
					<el-table-column v-for="(column, index) in columns" :key="index" :prop="column.field" :label="column.label"></el-table-column>
				</el-table>
				<el-pagination
					v-if="page"
					:current-page="state.page"
					:page-sizes="state.pageSizes"
					:page-size="state.limit"
					:total="state.total"
					layout="total, sizes, prev, pager, next, jumper"
					@size-change="sizeChangeHandle"
					@current-change="currentChangeHandle"
				>
				</el-pagination>
			</div>

			<template #footer>
				<el-button @click="handleClose">取消</el-button>
				<el-button type="primary" @click="submitHandle()">确定</el-button>
			</template>
		</el-dialog>
	</div>
</template>

<script setup lang="ts" name="MaDataTable">
import { ref, reactive, PropType, nextTick, watch } from 'vue'
import { IHooksOptions } from '@/hooks/interface'
import { useCrud } from '@/hooks'
import { ElMessage } from 'element-plus'

interface Column {
	field: string
	label: string
}
const queryRef = ref()
const handleReset = () => {
	state.queryForm = {}
	getDataList()
}
const props = defineProps({
	title: {
		type: String,
		required: true
	},
	url: {
		type: String,
		required: true
	},
	columns: {
		type: Array as PropType<Column[]>,
		required: true
	},
	queryParams: {
		type: Array as PropType<any[]>,
		required: false,
		default: () => []
	},
	multiple: {
		type: Boolean,
		required: false,
		default: false
	},
	rowKey: {
		type: String,
		required: false,
		default: ''
	},
	page: {
		type: Boolean,
		required: false,
		default: true
	},
	visible: {
		type: Boolean,
		default: false
	},
	method: {
		type: String,
		default: 'get'
	},
	defaultSelected: {
		type: Array as PropType<any[]>,
		required: false,
		default: () => []
	},
	queryForm: {
		type: Object as PropType<any>,
		required: false,
		default: () => ({})
	}
})

const state: IHooksOptions = reactive({
	dataListUrl: props.url,
	dataListMethods: props.method,
	queryForm: props.queryForm,
	isPage: props.page
})

const currentRow = ref()
const currentChange = (row: any) => {
	currentRow.value = row
}

const emit = defineEmits(['select', 'update:visible'])
const submitHandle = () => {
	// 多选
	if (props.multiple) {
		const dataList = state.dataListSelections ? state.dataListSelections : []
		if (dataList.length === 0) {
			ElMessage.warning('请选择记录')
			return
		}

		handleClose()
		emit('select', dataList)
		return
	}
	// 单选
	if (!currentRow.value) {
		ElMessage.warning('请选择记录')
		return
	}
	handleClose()
	emit('select', [currentRow.value])
}

const handleClose = () => {
	emit('update:visible', false)
}

const { getDataList, selectionChangeHandle, sizeChangeHandle, currentChangeHandle } = useCrud(state)

// 默认选中功能
const tableRef = ref()
const setDefaultSelection = () => {
	if (props.multiple && props.defaultSelected.length > 0 && state.dataList?.length > 0) {
		nextTick(() => {
			props.defaultSelected.forEach(selectedId => {
				const row = state.dataList?.find((item: any) => item[props.rowKey || 'id'] === selectedId)
				if (row && tableRef.value) {
					tableRef.value.toggleRowSelection(row, true)
				}
			})
		})
	}
}

// 设置默认选中
watch(
	() => state.dataList,
	() => {
		setDefaultSelection()
	},
	{ immediate: true }
)
</script>

<style lang="scss" scoped>
.ma-data-table {
	display: inline-block;

	.table-content {
		height: 60vh;
		display: flex;
		flex-direction: column;
	}
}
</style>
